import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {postAdminUserReplenishAccount} from '../../../api';
import * as colors from '../../../util/colors';
import AdminUserPicker, {formatAdminUserAmount} from '../../common/AdminUserPicker';
import Button from '../../common/Button';
import {ErrorAlert} from '../../common/Alerts';
import Paper from '../../common/Paper';

const getErrorMessage = async (response, fallback) => {
  try {
    const data = await response.json();
    return data.error || fallback;
  } catch (e) {
    return fallback;
  }
};

const parseRublesToKopeks = (value) => {
  const normalized = String(value || '')
    .replace(/\s+/g, '')
    .replace(',', '.')
    .trim();

  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Math.round(parsed * 100);
};

function AccountReplenishmentTab({authorization}) {
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [amountRub, setAmountRub] = React.useState('');
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [submitSuccess, setSubmitSuccess] = React.useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedUser) {
      setSubmitError('Сначала выберите пользователя');
      setSubmitSuccess('');
      return;
    }

    const amount = parseRublesToKopeks(amountRub);

    if (!Number.isInteger(amount) || amount <= 0) {
      setSubmitError('Укажите корректную сумму пополнения в рублях');
      setSubmitSuccess('');
      return;
    }

    setSubmitLoading(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      const response = await postAdminUserReplenishAccount(selectedUser.id, {authorization}, {amount});

      if ((response.status < 200) || (response.status >= 300)) {
        throw new Error(await getErrorMessage(response, 'Не удалось пополнить счет пользователя'));
      }

      const data = await response.json();
      const accountAmount = Number(data.account_amount || 0);

      setSelectedUser((prevState) => {
        if (!prevState) {
          return prevState;
        }

        return {
          ...prevState,
          account_amount: accountAmount,
        };
      });
      setAmountRub('');
      setSubmitSuccess(`Счет пользователя пополнен. Новый баланс: ${formatAdminUserAmount(accountAmount)} ₽`);
    } catch (error) {
      setSubmitError(error.message || 'Не удалось пополнить счет пользователя');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Paper>
      <Form onSubmit={handleSubmit}>
        <Heading>Пополнить личный счет</Heading>
        <Description>
          Выберите пользователя, укажите сумму в рублях и отправьте запрос в защищенный
          админский метод. В истории счета будет создана запись с комментарием <Code>manual</Code>.
        </Description>

        {submitError && (
          <AlertWrap>
            <ErrorAlert noTriangle>{submitError}</ErrorAlert>
          </AlertWrap>
        )}

        {submitSuccess && <SuccessAlert>{submitSuccess}</SuccessAlert>}

        <input type="hidden" value={selectedUser ? selectedUser.id : ''} readOnly />

        <AdminUserPicker
          authorization={authorization}
          selectedUser={selectedUser}
          onSelect={(user) => {
            setSelectedUser(user);
            setAmountRub('');
            setSubmitError('');
            setSubmitSuccess('');
          }}
        />

        {selectedUser && (
          <FieldGroup>
            <FieldLabel htmlFor="replenishment-amount">Сумма пополнения, ₽</FieldLabel>
            <FieldInput
              id="replenishment-amount"
              type="text"
              inputMode="decimal"
              value={amountRub}
              placeholder="Например, 1500"
              onChange={(event) => {
                setAmountRub(event.target.value);
                setSubmitError('');
                setSubmitSuccess('');
              }}
            />
            <HelperText>Сумма уходит в API в копейках. Можно указывать копейки через точку или запятую.</HelperText>
          </FieldGroup>
        )}

        <SubmitRow>
          <Button
            type="submit"
            disabled={submitLoading || !selectedUser}
            margin="0"
          >
            {submitLoading ? 'Зачисляем...' : 'Пополнить счет'}
          </Button>
        </SubmitRow>
      </Form>
    </Paper>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Heading = styled.h2`
  margin: 0;
  font-size: 20px;
  line-height: 1.2;
  color: ${colors.TEXT_TITLE};
`;

const Description = styled.div`
  margin-top: -12px;
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.TEXT_MINOR};
`;

const Code = styled.span`
  padding: 2px 6px;
  border-radius: 6px;
  background: ${colors.BG};
  color: ${colors.TEXT_MAIN};
  font-family: monospace;
  font-size: 13px;
`;

const AlertWrap = styled.div`
  max-width: 100%;
`;

const SuccessAlert = styled.div`
  padding: 14px 16px;
  border: 1px solid #D8ECCA;
  border-radius: 8px;
  background: #F4FBEE;
  color: ${colors.GREEN_DARK};
  font-size: 14px;
  line-height: 1.5;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  color: ${colors.TEXT_MAIN};
  font-weight: bold;
`;

const FieldInput = styled.input`
  width: 100%;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid ${colors.INPUT_STROKE};
  border-radius: 8px;
  outline: none;
  font-size: 15px;
  color: ${colors.TEXT_MAIN};
  background: white;

  &:focus {
    border-color: ${colors.BUTTON_BLUE};
    box-shadow: 0 0 0 3px rgba(71, 146, 255, 0.12);
  }
`;

const HelperText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.TEXT_MINOR};
`;

const SubmitRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

AccountReplenishmentTab.propTypes = {
  authorization: PropTypes.string,
};

AccountReplenishmentTab.defaultProps = {
  authorization: '',
};

export default AccountReplenishmentTab;
